import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftRight, ExternalLink, Search } from 'lucide-react'
import { useState } from 'react'

import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  SortableHead,
  TableCard,
  Toolbar,
} from '@/components/shell'
import { ExportButton } from '@/components/export-button'
import { api, type Paginated, type TradeRow } from '@/lib/api'
import { explorerTxUrl, formatDateTime, formatPrice, formatUnits, shortAddress } from '@/lib/format'

/** 支持从代币详情页带 tokenAddress 跳过来 */
type TradesSearch = { tokenAddress?: string; trader?: string }

export const Route = createFileRoute('/dashboard/trades')({
  validateSearch: (search: Record<string, unknown>): TradesSearch => ({
    tokenAddress: typeof search.tokenAddress === 'string' ? search.tokenAddress : undefined,
    trader: typeof search.trader === 'string' ? search.trader : undefined,
  }),
  component: TradesPage,
})

function TradesPage() {
  const { tokenAddress, trader } = Route.useSearch()

  const [page, setPage] = useState(1)
  const [side, setSide] = useState('all')
  const [sort, setSort] = useState<string | undefined>()
  const [address, setAddress] = useState(trader ?? '')

  const { data, isLoading } = useQuery({
    queryKey: ['trades', { page, side, sort, address, tokenAddress }],
    queryFn: () =>
      api.get<Paginated<TradeRow>>('/trades', {
        page,
        perPage: 25,
        side: side === 'all' ? undefined : side,
        sort,
        trader: address || undefined,
        tokenAddress,
      }),
  })

  return (
    <Page>
      <PageHeader
        title="成交记录"
        description={
          tokenAddress
            ? `筛选自代币 ${shortAddress(tokenAddress)} 的全部成交。`
            : '链上成交流水，可按方向、交易者与时间排序。数据来自索引器，只读。'
        }
        actions={
          <ExportButton
            filename="成交记录"
            fetchPage={(p, pp) =>
              api.get<Paginated<TradeRow>>('/trades', {
                page: p,
                perPage: pp,
                side: side === 'all' ? undefined : side,
                sort,
                trader: address || undefined,
                tokenAddress,
              })
            }
            columns={[
              { header: '时间', value: (t) => formatDateTime(t.blockTime) },
              { header: '方向', value: (t) => (t.side === 'buy' ? '买入' : '卖出') },
              { header: '代币', value: (t) => t.symbol ?? '' },
              { header: '合约地址', value: (t) => t.tokenAddress },
              { header: '交易者', value: (t) => t.trader },
              { header: '场馆', value: (t) => t.venue },
              { header: '计价币', value: (t) => t.quoteSymbol ?? t.quote },
              { header: '金额', value: (t) => formatUnits(t.quoteAmount, t.quoteDecimals, 6) },
              { header: '成交价', value: (t) => formatPrice(t.price, t.quoteDecimals, 8) },
              { header: '平台费', value: (t) => formatUnits(t.platformFee, t.quoteDecimals, 6) },
              { header: '项目费', value: (t) => formatUnits(t.projectFee, t.quoteDecimals, 6) },
              { header: '交易哈希', value: (t) => t.txHash },
            ]}
          />
        }
      />

      <Toolbar trailing={<Count value={data?.total} unit="笔" />}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-mut2" />
          <Input
            value={address}
            onChange={(e) => {
              setAddress(e.target.value)
              setPage(1)
            }}
            placeholder="交易者地址"
            className="h-9 w-72 rounded-apple pl-9 font-mono text-[12.5px]"
          />
        </div>

        <Select
          value={side}
          onValueChange={(v) => {
            setSide(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-28 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部方向</SelectItem>
            <SelectItem value="buy">买入</SelectItem>
            <SelectItem value="sell">卖出</SelectItem>
          </SelectContent>
        </Select>

        {tokenAddress && (
          <Link
            to="/dashboard/tokens/$address"
            params={{ address: tokenAddress }}
            className="text-[12.5px] text-cyan hover:underline"
          >
            返回代币详情
          </Link>
        )}
      </Toolbar>

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <TableHead className="section-label pl-6">方向</TableHead>
              <TableHead className="section-label">代币</TableHead>
              <TableHead className="section-label">交易者</TableHead>
              <TableHead className="section-label">场馆 / 费用</TableHead>
              <SortableHead field="quoteAmount" sort={sort} onSort={setSort} align="right">
                金额
              </SortableHead>
              <SortableHead field="price" sort={sort} onSort={setSort} align="right">
                成交价
              </SortableHead>
              <SortableHead field="blockTime" sort={sort} onSort={setSort}>
                时间
              </SortableHead>
              <TableHead className="section-label pr-6 text-right">交易哈希</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 10 }, (_, i) => (
                <TableRow key={i} className="border-line/40">
                  <TableCell colSpan={8} className="px-6 py-2.5">
                    <Skeleton className="h-7 w-full rounded-apple-sm" />
                  </TableCell>
                </TableRow>
              ))}

            {data?.data.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={8}>
                  <EmptyState icon={ArrowLeftRight} title="没有符合条件的成交记录" />
                </TableCell>
              </TableRow>
            )}

            {data?.data.map((t) => (
              <TableRow key={t.id} className="row-hover border-line/40">
                <TableCell className="pl-6">
                  <span
                    className={`inline-flex items-center gap-1.5 font-mono text-[11.5px] ${
                      t.side === 'buy' ? 'text-up' : 'text-dn'
                    }`}
                  >
                    <span
                      className="size-1.5 rounded-full"
                      style={{
                        backgroundColor:
                          t.side === 'buy' ? 'rgb(var(--up))' : 'rgb(var(--dn))',
                      }}
                    />
                    {t.side === 'buy' ? '买入' : '卖出'}
                  </span>
                </TableCell>

                <TableCell>
                  <Link
                    to="/dashboard/tokens/$address"
                    params={{ address: t.tokenAddress }}
                    className="text-[13px] text-txt hover:text-cyan"
                  >
                    {t.symbol ?? shortAddress(t.tokenAddress)}
                  </Link>
                </TableCell>

                <TableCell>
                  <Link
                    to="/dashboard/traders"
                    search={{ address: t.trader }}
                    className="font-mono text-[11.5px] text-mut hover:text-cyan"
                    title={t.trader}
                  >
                    {shortAddress(t.trader)}
                  </Link>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge className="h-5 bg-cyan/10 px-1.5 font-mono text-[10px] uppercase text-cyan hover:bg-cyan/10">
                      {t.venue}
                    </Badge>
                    <span className="font-mono text-[10.5px] text-mut2">
                      平 {formatUnits(t.platformFee, t.quoteDecimals, 4)} · 项{' '}
                      {formatUnits(t.projectFee, t.quoteDecimals, 4)}
                    </span>
                  </div>
                </TableCell>

                <TableCell className="tnum text-right font-mono text-[11.5px] text-txt">
                  {formatUnits(t.quoteAmount, t.quoteDecimals, 4)} {t.quoteSymbol ?? ''}
                </TableCell>
                <TableCell className="tnum text-right font-mono text-[11.5px] text-mut">
                  {formatPrice(t.price, t.quoteDecimals, 8)}
                </TableCell>
                <TableCell className="tnum font-mono text-[11px] text-mut2">
                  {formatDateTime(t.blockTime)}
                </TableCell>

                <TableCell className="pr-6 text-right">
                  <a
                    href={explorerTxUrl(t.txHash)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-[11px] text-mut2 hover:text-cyan"
                    title={t.txHash}
                  >
                    {shortAddress(t.txHash, 6, 4)}
                    <ExternalLink className="size-3" />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      <Pager page={page} pageCount={data?.pageCount ?? 1} onChange={setPage} />
    </Page>
  )
}
